<script>
  import { onMount } from "svelte";
  import {
    Page,
    Navbar,
    NavRight,
    Link,
    Card,
    CardHeader,
    CardContent,
    Row,
    Col,
    Button,
    Block,
    List,
    ListItem,
    Toggle,
    Range,
    f7,
  } from "framework7-svelte";
  import SoundAlertManager from "../utils/soundAlerts";
  import { BASE_URL, ENGINE_URL } from "../services/service.config";
  import EnvironmentSwitcher from "../components/EnvironmentSwitcher.svelte";
  import TradingNavbar from "../components/TradingNavbar.svelte";

  let environment = "LIVE";
  let strategies = [];
  let isLoading = false;
  let errorMessage = null;

  function handleEnvChange(newEnv) {
    environment = newEnv;
    sessionStorage.setItem("dealer_terminal_env", newEnv);
  }

  async function loadStrategies() {
    isLoading = true;
    errorMessage = null;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/strategies`);
      if (!res.ok) {
        throw new Error(`Failed to load strategies (HTTP ${res.status})`);
      }
      const json = await res.json();
      const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
      strategies = list.map(s => ({
        id: s.id || s.strategy_id,
        name: s.name || s.strategy_name || s.id,
        active: s.status === 'ACTIVE' || s.active !== false,
        capital: s.allocated_capital || s.capital || 100000,
        risk: s.risk_multiplier || s.risk || 1.0,
        winRate: s.win_rate || s.winRate || 0,
      }));
    } catch (err) {
      console.error("Error loading strategies:", err);
      strategies = [];
      errorMessage = err.message || "Failed to fetch strategies from server";
    } finally {
      isLoading = false;
    }
  }

  onMount(() => {
    loadStrategies();
  });

  async function toggleStrategy(strat) {
    const previousState = strat.active;
    strat.active = !strat.active;
    strategies = strategies;
    try {
      // 1. Update backend state
      await fetch(`${BASE_URL}/api/v1/strategies/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ strategy_id: strat.id, enabled: strat.active }),
      });

      // 2. Notify engine
      const res = await fetch(`${ENGINE_URL}/admin/strategy-killswitch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          strategy_id: strat.id,
          killed: !strat.active,
        }),
      });
      if (!res.ok) {
        throw new Error(`Engine returned status ${res.status}`);
      }
      f7.toast.create({ text: `${strat.name} is now ${strat.active ? 'ACTIVE' : 'HALTED'}`, closeTimeout: 2000 }).open();
    } catch (err) {
      console.error("Strategy toggle error:", err);
      strat.active = previousState;
      strategies = strategies;
      f7.toast.create({ text: `Failed to toggle ${strat.name}: ${err.message}`, closeTimeout: 3000 }).open();
    }
  }

  function openKillSwitchDialog() {
    const isSim = environment === "SIMULATION";
    const promptText = isSim
      ? "SIMULATION KILL SWITCH: This will immediately cancel all open simulated (SIM_*) orders and purge simulation sandbox state without touching live trading. Continue?"
      : "EMERGENCY LIVE KILL SWITCH: This will immediately cancel all open orders and send square-off commands across all LIVE production accounts on the Go Engine. Are you absolutely certain?";

    f7.dialog.confirm(
      promptText,
      isSim ? "RESET SIMULATION SANDBOX" : "CONFIRM LIVE PLATFORM HALT",
      async () => {
        try {
          f7.preloader.show();
          const endpoint = isSim ? `${ENGINE_URL}/sim/killswitch` : `${ENGINE_URL}/killswitch`;
          const res = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ active: true, reason: isSim ? "SIMULATION_SANDBOX_RESET" : "ADMIN_DASHBOARD_HALT" }),
          });
          f7.preloader.hide();
          if (!res.ok) {
            throw new Error(`Engine returned HTTP ${res.status}`);
          }
          SoundAlertManager.playChime("KILLSWITCH");
          f7.dialog.alert(
            isSim
              ? "Simulation Sandbox Reset: All SIM_* orders cancelled and simulation memory purged."
              : "Platform Kill Switch Engaged. All open orders cancelled, positions squaring off.",
            isSim ? "SANDBOX RESET" : "HALTED"
          );
        } catch (e) {
          f7.preloader.hide();
          console.error("Engine halt signal failed:", e);
          f7.dialog.alert(`CRITICAL ERROR: Failed to engage Kill Switch on engine (${e.message}). Please intervene manually.`, "KILL SWITCH FAILED");
        }
      }
    );
  }
</script>

<Page name="strategy-manager">
  <TradingNavbar title="Strategy & Risk Manager">
    <div style="display: flex; align-items: center; gap: 8px; margin-right: 8px;">
      <EnvironmentSwitcher
        {environment}
        on:change={(e) => handleEnvChange(e.detail)}
      />
    </div>
    <Button fill color="red" small onClick={openKillSwitchDialog}>
      EMERGENCY KILL SWITCH
    </Button>
  </TradingNavbar>

  <Block>
    {#if errorMessage}
      <Card class="bg-color-red text-color-white margin-bottom">
        <CardContent>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div>
              <strong>⚠️ Strategies API Error</strong>
              <div style="font-size: 13px; margin-top: 4px;">{errorMessage}</div>
            </div>
            <Button fill small color="white" text-color="red" onClick={loadStrategies}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    {/if}

    {#if !isLoading && strategies.length === 0 && !errorMessage}
      <Card>
        <CardContent style="text-align: center; color: #94a3b8; padding: 24px;">
          No strategies configured on server
        </CardContent>
      </Card>
    {/if}

    <Row>
      {#each strategies as strat}
        <Col width="100" medium="50">
          <Card>
            <CardHeader class="display-flex justify-content-space-between align-items-center">
              <div>
                <strong>{strat.name}</strong>
                <div class="text-color-gray font-size-12">ID: {strat.id}</div>
              </div>
              <Toggle checked={strat.active} onToggleChange={() => toggleStrategy(strat)} />
            </CardHeader>
            <CardContent>
              <div class="display-flex justify-content-space-between margin-bottom-half">
                <span>Historical Win Rate:</span>
                <strong class="text-color-green">{strat.winRate}%</strong>
              </div>
              <div class="margin-bottom">
                <div class="display-flex justify-content-space-between">
                  <span>Allocated Capital:</span>
                  <strong>₹{strat.capital.toLocaleString('en-IN')}</strong>
                </div>
                <Range
                  min={10000}
                  max={1000000}
                  step={10000}
                  value={strat.capital}
                  onRangeChange={(val) => {
                    strat.capital = Number(val);
                    strategies = strategies;
                  }}
                />
              </div>
              <div>
                <div class="display-flex justify-content-space-between">
                  <span>Risk Multiplier:</span>
                  <strong>{Number(strat.risk).toFixed(1)}x</strong>
                </div>
                <Range
                  min={0.5}
                  max={3.0}
                  step={0.1}
                  value={strat.risk}
                  onRangeChange={(val) => {
                    strat.risk = Math.round(Number(val) * 10) / 10;
                    strategies = strategies;
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </Col>
      {/each}
    </Row>
  </Block>
</Page>
